import { useEffect, useRef } from "react";

export default function AlpineEffects() {
  const snowRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion) return;

    const snowCanvas = snowRef.current;
    if (!snowCanvas) return;
    const snowCtx = snowCanvas.getContext("2d");
    if (!snowCtx) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);

    type Flake = { x: number; y: number; r: number; speed: number; drift: number; opacity: number };
    let snowflakes: Flake[] = [];
    const SNOW_COUNT = 80;

    const makeFlake = (randomY = false): Flake => ({
      x: Math.random() * window.innerWidth,
      y: randomY ? Math.random() * window.innerHeight : -10,
      r: 0.8 + Math.random() * 1.8,
      speed: 0.4 + Math.random() * 1.0,
      drift: -0.25 + Math.random() * 0.5,
      opacity: 0.3 + Math.random() * 0.4,
    });

    const initSnow = () => {
      snowflakes = [];
      for (let i = 0; i < SNOW_COUNT; i++) snowflakes.push(makeFlake(true));
    };

    const drawSnow = () => {
      snowCtx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      snowflakes.forEach((f) => {
        snowCtx.beginPath();
        snowCtx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        snowCtx.fillStyle = `rgba(245, 250, 253,${f.opacity})`;
        snowCtx.fill();
        f.y += f.speed;
        f.x += f.drift;
        if (f.y > window.innerHeight + 10 || f.x < -20 || f.x > window.innerWidth + 20) {
          Object.assign(f, makeFlake(false));
        }
      });
      const progress = Math.min(window.scrollY / (window.innerHeight * 0.72), 1);
      snowCanvas.style.opacity = String(Math.max(0, 1 - progress));
    };

    const resize = () => {
      snowCanvas.width = window.innerWidth * dpr;
      snowCanvas.height = window.innerHeight * dpr;
      snowCanvas.style.width = window.innerWidth + "px";
      snowCanvas.style.height = window.innerHeight + "px";
      snowCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    let rafId = 0;
    const loop = () => {
      drawSnow();
      rafId = requestAnimationFrame(loop);
    };

    resize();
    initSnow();
    window.addEventListener("resize", resize);
    rafId = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={snowRef}
      className="fixed inset-0 w-screen h-screen pointer-events-none transition-opacity duration-300"
      style={{ zIndex: 9996 }}
    />
  );
}